import { autoinject, transient, observable, computedFrom } from "aurelia-framework";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { MessageCentreService } from "../../../services/message-centre.service";
import { AddSubtaskRequest, CorrespondenceEmailModel, NewConversationCheckboxActionModel } from "../../../models/message-centre.models";
import { AttachmentFile } from "@rightship-tech/common-ui/models/attachments.models";
import { EmailRecipientTypeEnum } from "../../../models/common.models";
import { ValidationController, ValidationControllerFactory, ValidationRules } from 'aurelia-validation';
import { LookupValue } from "@rightship-tech/common-ui/models/lookup.models";
import { CustomValidationRenderer } from '@rightship-tech/common-ui/client-validation-system/custom-validation-renderer';
import { RegexConstants } from '@rightship-tech/common-ui/constants/regex.constants';
import { RecipienthHelper } from "../../../helpers/recipient.helper";
import { EntityTypes, SubtaskSourceEnum } from "@rightship-tech/common-ui/models/basic.models";

@autoinject
@transient()
export class DialogSendMessage {

    private _subtaskRequestModel: AddSubtaskRequest;
    private _validator: ValidationController;
    public subject: string;
    public accept: string = "application/pdf,image/png,image/jpeg,image/jpg,application/zip,application/x-zip-compressed,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/vnd.ms-outlook,.msg";
    public correspondenceMessage: string;
    public readonly defaultRightShipRecipient: string = "RightShip";
    public displayName = "Select recipients";
    public attachments: AttachmentFile[] = [];
    public loading: boolean = false;
    public emails: CorrespondenceEmailModel[] = [];
    public entityId: number;
    public entityTypeId: number;
    public emailIdentifier?: string;
    public afterSave: () => Promise<void>;
    public canEditRightshipRecipient: boolean | null;
    public addedRsAsRecipient: boolean;
    public changeStatusToAwaitingResponse: boolean = false;
    public genericCheckBoxAction: NewConversationCheckboxActionModel = null;
    public genericCheckBoxValue: boolean = false;
    public onRsRecipientToggle: (x) => void;

    @observable
    public selectedItems: LookupValue[] = [];

    constructor(validationControllerFactory: ValidationControllerFactory, public dialogClientService: DialogClientService,
        private messageCentreService: MessageCentreService) {
        this._validator = validationControllerFactory.createForCurrentScope();
        this._validator.addRenderer(new CustomValidationRenderer());
    }
    
    activate(model: {entityId: number, entityTypeId: number, emailIdentifier?: string, afterSaved: () => Promise<void>, 
        canEditRightshipRecipient: boolean, checkBoxAction?: NewConversationCheckboxActionModel, addedRsAsRecipient?: boolean, onRsRecipientToggle: (x) => void}) {
        
        this.entityId = model.entityId;
        this.entityTypeId = model.entityTypeId;
        this.emailIdentifier = model.emailIdentifier;        
        this.afterSave = model.afterSaved;
        this.genericCheckBoxAction = model.checkBoxAction;
        if (this.genericCheckBoxAction) {
            this.genericCheckBoxValue = this.genericCheckBoxAction.defaultValue;
        }

        //Rightship recipient config
        this.canEditRightshipRecipient = model?.canEditRightshipRecipient ?? null;
        this.addedRsAsRecipient = model?.addedRsAsRecipient;
        this.onRsRecipientToggle = model?.onRsRecipientToggle;

        this.addEmail()
    }

    @computedFrom("entityTypeId")
    get showRecipientList() {
        return this.entityTypeId === EntityTypes.PSC || this.entityTypeId == EntityTypes.RightShipInspection;
    }

    @computedFrom("genericCheckBoxAction", "genericCheckBoxAction.isHidden")
    get showGenericCheckbox() {
        if (!this.genericCheckBoxAction) return false;
        return this.genericCheckBoxAction !== null && !this.genericCheckBoxAction?.isHidden && this.genericCheckBoxAction?.action !== null;
    }

    setUpValidation() {
        this.getRules();
    }

    getRules(){
        const emailRegex = new RegExp(RegexConstants.Patterns.EmailRfc5322);

        ValidationRules

            .ensure("emails")
            .minItems(1)
            .withMessage("Please add a recipient")

            .ensure("subject")
            .required()
            .withMessage("Please enter subject")

            .ensure("correspondenceMessage")
            .required()
            .withMessage("Please enter message")

            .ensure('selectedItems')
            .required()
            .satisfies(() => this.showRecipientList)
            
            .on(this);

        this.emails.forEach((email) => {
            ValidationRules
                .ensure('email')
                .required()
                .satisfies(email => {
                    return emailRegex.test(email)
                })
                .withMessage('Invalid Email - Acceptable format is <i>username@domain.com</i>')
                .on(email);
        });
    }

    selectedItemsChanged() {

        if(!this.emails) return;
        
        //Exclude the free text emails from the selected items
        const freeTextEmails = this.emails.filter(c => !c.recipientTypeName);
        
        this.emails = this.selectedItems.map(c => {
            const email = { 
                email: c.additionalData.email, 
                emailRecipientTypeId: EmailRecipientTypeEnum.To, 
                recipientTypeName: c.additionalData.recipientType 
            };
            return RecipienthHelper.checkRecipientType(email);
        });
        this.emails.push(...freeTextEmails);
    }

    async submit() {
        if (this.correspondenceMessage && !$(this.correspondenceMessage).get(0).innerText.trim()) {
            this.correspondenceMessage = "";
        }
        this.setUpValidation();
        const validator = await this._validator.validate();
        if (validator.valid) {
            let isRightShipAlerted = true;
            if (this.canEditRightshipRecipient != null) {
                isRightShipAlerted = this.canEditRightshipRecipient ? this.addedRsAsRecipient : false; 
            }
            try {
                this.loading = true;
                this._subtaskRequestModel = {
                    entityId: this.entityId,
                    entityTypeId: this.entityTypeId,
                    emails: this.emails,
                    isInternal: false,
                    resendEmail: false,
                    skipInvitation: true,
                    isCustomerRFI: true,
                    changeStatusToPendingReview: false,
                    changeStatusToAwaitingResponse: this.changeStatusToAwaitingResponse,
                    message: this.correspondenceMessage,
                    comment: "",
                    attachments: [],
                    files: this.getAttachmentTypes(),
                    statusSnippet: "",
                    name: "",
                    submit: true,
                    subtaskId: null,
                    subtaskTypes: [],
                    subtaskCorrespondenceId: null,
                    vetTaskIds: [],
                    dataSourceId: 0,
                    isPriority: false,
                    messageSenderEmail: this.emailIdentifier,
                    subject: this.subject,
                    subtaskSourceId: SubtaskSourceEnum.MessageCentre,
                    isRightShipAlerted: isRightShipAlerted
                }
                if (this.genericCheckBoxAction) {
                    await this.genericCheckBoxAction.action(this._subtaskRequestModel, this.genericCheckBoxValue);
                }
                await this.messageCentreService.addSubtask(this._subtaskRequestModel);
                if(this.afterSave) await this.afterSave();
                this.loading = false;
            } catch (error) {
                console.error(error);
                this.loading = false;
            }
        }
    }

    cancel() {
        this.dialogClientService.hide();
    }

    removeEmail(i: number, email: CorrespondenceEmailModel) {
        this.emails.splice(i, 1);
        this.selectedItems = this.selectedItems.filter(x => x.additionalData.email !== email.email || x.additionalData.recipientType !== email.recipientTypeName);

        if(this.emails.length === 0 && !this.addedRsAsRecipient) {
            
            this.addEmail()
        }
    }

    addEmail() {
        this.emails.push({ email: "", emailRecipientTypeId: EmailRecipientTypeEnum.To, recipientTypeName: "" });
    }

    getAttachmentTypes(){
        if(this.attachments && this.attachments.length > 0)
        {
            const attachmentFiles = this.attachments.map(c => {
                return { attachementTypeId: c.attachmentTypeId, file: c as File }
            });
            return attachmentFiles;
        }

        return [];
    }

    toggleRsAsRecipient(add: boolean) {
       this.onRsRecipientToggle(add);
        
        this.addedRsAsRecipient = add;

        if(!this.addedRsAsRecipient && this.emails.length === 0) {
            this.addEmail();
        }
    }
}
